import { useState } from "react";
import type { IncidentTimelineConfig } from "../scenario/types";
import { TimelineIcon } from "../ui/TimelineIcons";
import { useFitScale } from "../ui/useFitScale";
import { tx } from "../lib/tx";

/**
 * Consequence Room review list. 2026-07-14 redesign: events now unlock
 * strictly in order (item N only clickable once item N-1 has been opened)
 * so the room reads as a guided walk through Max's day rather than a
 * free-for-all list, and the quiz is gated behind reviewing EVERY event
 * (not just 3 of them, the old bar) so nobody skips the recap.
 */
export function IncidentTimeline({
  config,
  onDone,
}: {
  config: IncidentTimelineConfig;
  onDone: () => void;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [seen, setSeen] = useState<Set<string>>(() => new Set());
  const open = config.events.find((e) => e.id === openId) ?? null;
  const total = config.events.length;
  const optional = !!config.optional;
  const allSeen = seen.size >= total;
  const nextIndex = config.events.findIndex((e) => !seen.has(e.id));

  // 2026-07-15: the numbered-badge + "how this works" banner redesign added
  // enough height that this panel started overflowing (scrollbar) on
  // shorter browser windows. Same fix as the WiFi terminal -- measure the
  // real content height and shrink a scale variable to guarantee it always
  // fits `.rw-panel.incidenttimeline`'s box instead of hand-tuning padding
  // for one specific window size.
  const { containerRef: fitContainerRef, contentRef: fitContentRef } = useFitScale<HTMLDivElement>("itl", [
    config,
    total,
    open?.id,
  ]);

  const select = (id: string, index: number) => {
    const unlocked = optional || index === 0 || seen.has(config.events[index - 1].id);
    if (!unlocked) return;
    setOpenId(id);
    setSeen((s) => new Set(s).add(id));
  };

  return (
    <div className="rw-panel incidenttimeline" ref={fitContainerRef}>
      <div className="itl-fit" ref={fitContentRef}>
      <div className="itl-head">
        <div>
          <div className="rw-kicker">{config.kicker ?? tx("Consequence room")}</div>
          <h2>{config.headline ?? tx("Max's day — risk timeline")}</h2>
          {config.body && <p className="rw-body">{config.body}</p>}
          <p className="itl-howto">
            <strong>{tx("How this works:")}</strong>{" "}
            {config.howTo ??
              tx("Click each numbered event on the left, in order, to see what happened and why it mattered. Once every event is reviewed, Continue will unlock.")}
          </p>
        </div>
        <div className="itl-progress" aria-label={tx("{size} of {total} reviewed", { size: seen.size, total })}>
          <svg viewBox="0 0 44 44" className="itl-progress-ring">
            <circle cx="22" cy="22" r="19" className="itl-progress-track" />
            <circle
              cx="22"
              cy="22"
              r="19"
              className="itl-progress-fill"
              style={{ strokeDashoffset: `${119.4 * (1 - seen.size / total)}` }}
            />
          </svg>
          <div className="itl-progress-text">
            <strong>{seen.size}</strong>/{total}
          </div>
        </div>
      </div>

      <div className="itl-layout">
        <ol className="itl-list">
          {config.events.map((e, i) => {
            const isSeen = seen.has(e.id);
            const unlocked = optional || i === 0 || seen.has(config.events[i - 1].id);
            const isNext = i === nextIndex;
            return (
              <li key={e.id}>
                <button
                  type="button"
                  className={`itl-item ${openId === e.id ? "on" : ""} ${isSeen ? "seen" : ""} ${
                    !unlocked ? "locked" : ""
                  } ${isNext ? "next" : ""}`}
                  disabled={!unlocked}
                  onClick={() => select(e.id, i)}
                >
                  <span className="itl-num" aria-hidden>{i + 1}</span>
                  <span className="itl-ico" aria-hidden>
                    {!unlocked ? <TimelineIcon kind="lock" /> : <TimelineIcon kind={e.icon} />}
                  </span>
                  <span className="itl-item-title">{e.title}</span>
                  {isSeen && (
                    <span className="itl-check" aria-hidden>
                      {"\u2713"}
                    </span>
                  )}
                  {isNext && !isSeen && <span className="itl-next-badge">{tx("Up next")}</span>}
                </button>
              </li>
            );
          })}
        </ol>
        {open ? (
          <div className="itl-detail">
            <div className="itl-detail-ico" aria-hidden>
              <TimelineIcon kind={open.icon} />
            </div>
            <h3>{open.title}</h3>
            <p>
              <strong>{config.labels?.what ?? tx("What happened")}</strong>
              <br />
              {open.what}
            </p>
            <p>
              <strong>{config.labels?.why ?? tx("Why it mattered")}</strong>
              <br />
              {open.why}
            </p>
            <p>
              <strong>{config.labels?.fix ?? tx("What should have been done")}</strong>
              <br />
              {open.fix}
            </p>
          </div>
        ) : (
          <div className="itl-detail itl-detail-empty">
            <span className="itl-detail-empty-ico" aria-hidden>
              <TimelineIcon kind="list" />
            </span>
            <p>
              {config.emptyPrompt ?? tx("Click the first event on the left to begin the reconstruction.")}
            </p>
          </div>
        )}
      </div>
      <button
        type="button"
        className={`btn itl-continue ${optional || allSeen ? "itl-continue-ready" : ""}`}
        onClick={onDone}
        disabled={!optional && !allSeen}
      >
        {optional
          ? config.continueLabel ?? tx("Continue")
          : allSeen
            ? config.continueLabel ?? tx("Continue to quiz")
            : tx("Review all {total} events to continue ({size}/{total2})", { total, size: seen.size, total2: total })}
      </button>
      </div>
    </div>
  );
}
